import { DAYS_OF_WEEK } from '../schemas/menu-item-schema';
import type {
  TAvailabilitySlot,
  TAvailabilityTiming,
} from '../schemas/menu-item-schema';
import type { TAvailabilityRule, TMenuItem, TMenuItemSlim, TModifierRecord } from '../types/menu-types';
import type { TModifier, TModifierGroup } from '../../modifiers/types';

export const DEFAULT_START_TIME = '09:00';
export const DEFAULT_END_TIME = '21:00';

// ─── Availability helpers ─────────────────────────────────────────────────────

export const createDefaultAvailabilitySlots = (): TAvailabilitySlot[] =>
  DAYS_OF_WEEK.map((day) => ({ day, enabled: false, timings: [] }));

export const createDefaultTiming = (): TAvailabilityTiming => ({
  from: DEFAULT_START_TIME,
  to: DEFAULT_END_TIME,
});

export const createEmptyTiming = (): TAvailabilityTiming => ({ from: '', to: '' });

/** Convert API availabilityRules (multi-day per rule) to per-day slot model */
export const buildSlotsFromMenuItem = (menuItem: TMenuItem): TAvailabilitySlot[] => {
  const slots = createDefaultAvailabilitySlots();
  for (const rule of menuItem.availabilityRules ?? []) {
    for (const day of rule.days) {
      const slot = slots.find((s) => s.day === day);
      if (slot) {
        slot.enabled = true;
        slot.timings.push({ from: rule.from, to: rule.to });
      }
    }
  }
  return slots;
};

/** Convert per-day slots back to API availabilityRules format */
export const convertSlotsToRules = (slots: TAvailabilitySlot[]): TAvailabilityRule[] =>
  slots
    .filter((slot) => slot.enabled && slot.timings.length > 0)
    .flatMap((slot) =>
      slot.timings
        .filter((t) => t.from && t.to)
        .map((t) => ({ days: [slot.day] as TAvailabilityRule['days'], from: t.from, to: t.to }))
    );

// ─── Modifier helpers ─────────────────────────────────────────────────────────

/**
 * Flatten modifier containers into picker view records.
 * Each container._id is the link ID stored on menuItem._idModifiers.
 * Real group data (header, items, flags) lives in the nested modifiers array.
 */
export const buildModifierRecords = (
  allModifiers: TModifier[],
  menuItems: TMenuItemSlim[],
  categories: { _id: string; name: string }[]
): TModifierRecord[] => {
  const menuItemsById = new Map(menuItems.map((item) => [item._id, item]));
  const categoriesById = new Map(categories.map((cat) => [cat._id, cat]));

  return allModifiers.flatMap((container) => {
    const groups: TModifierGroup[] = container.modifiers?.length
      ? container.modifiers
      : [container];

    return groups.map((group) => {
      const optionItems = (group.items ?? []).reduce<
        { _id: string; name: string; price: { value: number; currency: string } }[]
      >((acc, itemId) => {
        const item = menuItemsById.get(itemId);
        if (item) {
          acc.push({
            _id: item._id,
            name: item.name,
            price: item.price ?? { value: 0, currency: 'GBP' },
          });
        }
        return acc;
      }, []);

      const attachedCategoryIds = new Set(
        menuItems
          .filter((item) => item._idModifiers?.includes(container._id))
          .map((item) => item._idCategory)
          .filter((id): id is string => Boolean(id))
      );

      const attachedCategoryNames = [...attachedCategoryIds]
        .map((id) => categoriesById.get(id)?.name)
        .filter((name): name is string => Boolean(name));

      return {
        _idModifier: container._id,
        header: group.header,
        required: group.required,
        multiSelection: group.multiSelection,
        extraAllowed: group.extraAllowed,
        addUpPrices: group.addUpPrices,
        defaultSelection: group.defaultSelection ?? null,
        optionItems,
        attachedCategoryNames,
      };
    });
  });
};

/** Extract a fileId from a full image URL (last path segment) */
export const imageUrlToFileId = (url: unknown): string => {
  if (typeof url !== 'string' || !url) return '';
  const i = url.lastIndexOf('/');
  return i >= 0 ? url.substring(i + 1) : url;
};
